import { toast } from 'react-toastify';
import { GetLocalStorage } from '../misc/UseLocalStorage';

export type UserLevel =
  | 'superadmin'
  | 'admin'
  | 'peserta'
  | 'adminhelpdesk'
  | 'admintte'
  | 'adminmitrakerjalembaga'
  | 'adminmitrakerjanonlembaga';

let serverVerifiedLevel: string | undefined;
let verificationAttempted = false;

/**
 * Di-set oleh useVerifyRole setelah fetch auth/me berhasil.
 * Menjadi source of truth agar role tidak bisa dimanipulasi via localStorage.
 */
export const setServerVerifiedLevel = (level: string): void => {
  serverVerifiedLevel = level;
  verificationAttempted = true;
};

/**
 * Di-panggil oleh useVerifyRole saat auth/me gagal atau tidak ada token.
 * Mengaktifkan deny-by-default: getLevel() return undefined → isAdminReadOnly true.
 */
export const markVerificationFailed = (): void => {
  verificationAttempted = true;
};

/**
 * Sebelum verifikasi server selesai: pakai localStorage sementara (agar UX tidak rusak saat load).
 * Setelah verifikasi: pakai server value. Jika gagal: return undefined → deny by default.
 */
export const getLevel = (): string | undefined => {
  if (!verificationAttempted) {
    return GetLocalStorage({ name: 'level' });
  }
  return serverVerifiedLevel;
};

export const isSuperAdmin = (): boolean => getLevel() === 'superadmin';

/**
 * Admin biasa dan Admin Helpdesk bersifat read-only — hanya boleh GET,
 * tidak boleh tambah/edit/hapus/submit apapun.
 * Jika verifikasi server gagal (level undefined) → deny by default (return true).
 */
export const isAdminReadOnly = (): boolean => {
  const level = getLevel();
  if (level === undefined) return verificationAttempted;
  return level === 'admin' || level === 'adminhelpdesk';
};

/**
 * True bila user boleh melakukan aksi mutasi (tambah/edit/hapus/submit).
 * False khusus untuk admin read-only.
 */
export const canMutate = (): boolean => !isAdminReadOnly();

/**
 * Whitelist endpoint yang tetap boleh diakses oleh admin read-only
 * untuk SEMUA method mutasi (POST/PUT/PATCH/DELETE).
 * Contoh: logout, login — operasi auth dasar.
 */
const READONLY_WHITELIST: RegExp[] = [
  /\/auth\/logout/i,
  /\/logout/i,
  /\/auth\/login/i,
];

/**
 * Whitelist endpoint yang boleh diakses admin read-only hanya untuk
 * method POST/PUT/PATCH (BUKAN DELETE). Untuk endpoint yang perlu
 * izin edit tapi tetap melarang hapus.
 */
const READONLY_WHITELIST_WRITE: RegExp[] = [
  /soal\/\d+/i,
  /user\/reset-default-password\//i,
];

export const isReadOnlyWhitelisted = (
  url: string,
  method: string
): boolean => {
  if (!url) return false;
  if (READONLY_WHITELIST.some((pattern) => pattern.test(url))) return true;
  const m = (method || '').toLowerCase();
  if (m === 'delete') return false;
  return READONLY_WHITELIST_WRITE.some((pattern) => pattern.test(url));
};

/**
 * True bila request mutasi pada URL ini harus diblokir untuk user saat ini.
 */
export const shouldBlockMutation = (method: string, url: string): boolean => {
  if (!isAdminReadOnly()) return false;
  const m = (method || 'get').toLowerCase();
  if (m === 'get' || m === 'head' || m === 'options') return false;
  return !isReadOnlyWhitelisted(url, m);
};

export const READONLY_ADMIN_MESSAGE =
  'Akun admin bersifat read-only. Aksi tidak diizinkan.';

// De-duplikasi toast: kalau banyak request mutasi diblokir berturut-turut
// (mis. form auto-save), kita hanya tampilkan satu toast dalam jendela
// waktu pendek supaya tidak spam.
let lastReadOnlyToastAt = 0;

export const notifyReadOnlyBlocked = (): void => {
  const now = Date.now();
  if (now - lastReadOnlyToastAt < 1500) return;
  lastReadOnlyToastAt = now;
  try {
    toast.error(READONLY_ADMIN_MESSAGE, {
      style: { boxShadow: '0px 1px 6px #F86E70' },
    });
  } catch (_) {
    // toast container belum mount — abaikan
  }
};

/**
 * Bentuk error yang konsisten dengan shape AxiosError sehingga catch block
 * existing tetap bisa membaca `error.response.status` / `error.response.data`.
 */
export const buildReadOnlyError = (config?: any) => ({
  config,
  isReadOnlyAdmin: true,
  response: {
    status: 403,
    data: {
      message: READONLY_ADMIN_MESSAGE,
      code: 'READONLY_ADMIN',
    },
  },
});
